import React from 'react'
import withRedux from 'next-redux-wrapper'
import parse from 'lib/url/parse'
import Theme from 'components/Theme'
import { App, Store } from 'apps/public/browse'
import { Actions as PageActions } from 'actions/page'
import { Actions as AlgoliaActions } from 'actions/algolia2/data'
import { getDataLayer } from 'apps/public/browse/selectors/dataLayer'
import { getCheckSeoContentLoaded, getMetaData } from 'apps/public/browse/selectors/seoContent'
import { getPageParamsQuery } from 'apps/public/browse/helpers/url'
import { getSortIndice } from 'apps/public/browse/helpers/constants'
import { checkPreviousAlgoliaFacets } from 'apps/public/browse//helpers/facets'
import { fetchPLPSeoContent } from 'apis/seoContent'
import { getPathParams } from 'apps/public/browse/selectors/app'
import { getSlugifyDictionary } from 'selectors/algolia2/data'
import { algoliaBrowseApi, algoliaEventsApi } from 'apis/algolia'
import { storeSetup } from 'lib/pageSetup'
import {
  parseUrlFacets,
  selectedFacetsToAlgoliaQueryString,
  selectedFacetsToAlgoliaEventFilter,
} from 'lib/algolia/v2'
import { getMeasurementUnit, getUserID } from 'selectors/user/session'
import { getUserPreferencesDestinationZone, getAlgoliaUserToken } from 'selectors/user/cookie'
import { getRandomizedSponsoredItems } from 'lib/sponsoredItems'
import { setAlgoliaQueryIDInLocalStorage } from 'lib/algolia/v2/helpers.js'
import { browseBooleanFacets } from 'lib/algolia/v2/constants'

const HITS_PER_PAGE = 50
function Browse(): React.ReactElement<React.ComponentProps<any>, any> {
  return (
    <Theme>
      <App />
    </Theme>
  )
}

Browse.clientSideTransitions = async ({ store }) => {
  if (window) {
    store.dispatch(PageActions.IS_READY(false))
    const { href } = window.location
    const pageParams = getPageParamsQuery(href)
    const { hitsPerPage, page, query, sort } = pageParams
    const state = { ...store.getState() }
    // const searchIndice = getAlgoliaSearchIndice(state)
    const slugifyDictionary = getSlugifyDictionary(state)
    const pathParams = getPathParams(state)
    const userToken = getAlgoliaUserToken(state)
    const userID = getUserID(state)
    const destinationZone = getUserPreferencesDestinationZone(state)

    // Create Facets List for Redux Store, we will merge in URL Params & Queries ( slugify )
    const selectedFacets = parseUrlFacets({
      dictionary: slugifyDictionary,
      href,
      routeParams: pathParams,
      booleanFacets: browseBooleanFacets,
    })
    // We only want AVAIL => FOR SALE artworks - Manually add query, Ignore Prints
    const hasPrints = selectedFacets.has_prints.length >= 1
    const overrideFacets = {
      ...(!hasPrints
        ? {
            original_status: 'avail',
          }
        : {}),
      ...selectedFacets,
    }
    const filters = selectedFacetsToAlgoliaQueryString(overrideFacets, {
      measurementUnit: getMeasurementUnit(state),
      destinationZone,
    })
    const searchIndice = getSortIndice(sort, destinationZone, hasPrints)
    // We have the default Indice, but we need to apply SORT logic, if it exists
    const algoliaResults = await algoliaBrowseApi({
      filters,
      hitsPerPage: hitsPerPage || HITS_PER_PAGE,
      page,
      query,
      searchIndice,
      userToken,
      destinationZone,
    })
    // store ALGOLIA_QUERY_ID in local storage
    const { queryID } = algoliaResults
    setAlgoliaQueryIDInLocalStorage(queryID)
    // Track Facet Changes
    const algoliaChanges = checkPreviousAlgoliaFacets({
      selectedFacets,
      store: state,
    })
    const eventFilters = selectedFacetsToAlgoliaEventFilter(selectedFacets)
    const { changedFacets, previousAlgoliaFacets } = algoliaChanges
    const sponsoredItems = getRandomizedSponsoredItems()
    // Let's create a LIST of ACTIONS to dispatch
    const storeActions = [
      AlgoliaActions.UPDATE({
        changedFacets,
        data: algoliaResults,
        previousAlgoliaFacets,
        searchIndice,
        selectedFacets,
      }),
      PageActions.SET({
        path: parse(href).path || '',
        params: pageParams,
      }),
      PageActions.IS_READY(true),
      PageActions.SET_DATA({
        sponsoredItems,
      }),
    ]
    // We want to check if we have ALL seo content else we should FETCH more
    const hasLoaded = getCheckSeoContentLoaded(state)

    if (!hasLoaded) {
      const { payload: seoContent, success } = await fetchPLPSeoContent()

      if (success) {
        storeActions.push(
          PageActions.SET_DATA({
            seoContent,
          })
        )
      }
    }

    // Dispatch Array<ACTIONS>
    store.dispatch(storeActions)
    // Data Layer
    const dataLayer = getDataLayer({ ...store.getState() })
    window.dataLayer.push({ ...dataLayer, event: 'pageview' })
    // Algolia event
    algoliaEventsApi({
      events: [
        {
          eventName: 'select_filter',
          eventType: 'click',
          index: searchIndice,
          userToken: userToken,
          authenticatedUserToken: String(userID) || '',
          ...(eventFilters ? { filters: [eventFilters] } : { filters: ['none: none'] }),
          ...(queryID && {
            queryID: queryID,
          }), // pass queryID only if its present, otherwise do not pass
        },
      ],
    })
  }
}

Browse.getInitialProps = async ({ req, store, res, query }) => {
  // Client-Side
  if (!req) {
    await Browse.clientSideTransitions({
      store,
      query,
    })
    const state = {
      ...store.getState(),
    }
    const metaData = getMetaData(state)
    return {
      metaData,
    }
  }

  // SSR
  storeSetup(store, req)
  const { location } = req
  const { algoliaSetup, page } = query
  const {
    data: { pathParams },
  } = page

  // Lets FETCH Algolia data
  const { sharedFacetNames, slugifyDictionary } = algoliaSetup
  const selectedFacets = parseUrlFacets({
    dictionary: slugifyDictionary,
    href: location.href,
    routeParams: pathParams,
  })
  store.dispatch(AlgoliaActions.UPDATE({ sharedFacetNames, slugifyDictionary, selectedFacets }))

  // Page Data Setup
  store.dispatch(PageActions.SET(page))
  const { storeLocale } = req
  const storeTag = storeLocale === 'en-us' ? `${storeLocale}, en-global` : `${storeLocale}`
  res.set('Edge-Cache-Tag', `page-browse, ${storeTag}`)
  // Metadata & Datalayer
  const state = {
    ...store.getState(),
  }
  const metaData = getMetaData(state)
  const dataLayer = getDataLayer(state)

  return {
    dataLayer,
    metaData,
  }
}

export default withRedux(Store)(Browse)
